import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import { PropertyOverlay } from '../components/PropertyOverlay';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

const AREA_CATEGORIES = [
  { id: 'hunting', label: 'Hunting', color: '#28A745', fill: 'rgba(40,167,69,0.3)' },
  { id: 'fishing', label: 'Fishing', color: '#007BFF', fill: 'rgba(0,123,255,0.3)' },
  { id: 'restricted', label: 'Restricted', color: '#DC3545', fill: 'rgba(220,53,69,0.3)' },
  { id: 'other', label: 'Other', color: '#888', fill: 'rgba(136,136,136,0.3)' },
];
const CAT_MAP = new window.Map(AREA_CATEGORIES.map((c) => [c.id, c]));

interface Area {
  id: string;
  name: string;
  category: string;
  boundary: Array<{ lat: number; lon: number }>;
}

type PanelMode =
  | { type: 'none' }
  | { type: 'drawing' }
  | { type: 'create' }
  | { type: 'edit'; area: Area }
  | { type: 'view'; area: Area };

// ---------------------------------------------------------------------------
// Polygon Renderer
// ---------------------------------------------------------------------------

function AreaPolygons({ areas, activeId, drawingPath, editId, editBoundary, onAreaClick }: {
  areas: Area[];
  activeId: string | null;
  drawingPath: Array<{ lat: number; lng: number }>;
  editId?: string | null;
  editBoundary?: Array<{ lat: number; lon: number }>;
  onAreaClick: (area: Area) => void;
}) {
  const map = useMap();
  const overlaysRef = useRef<(google.maps.Polygon | google.maps.Polyline)[]>([]);

  useEffect(() => {
    if (!map) return;
    overlaysRef.current.forEach((p) => p.setMap(null));
    overlaysRef.current = [];

    for (const area of areas) {
      const boundaryToUse = (editId && area.id === editId && editBoundary) ? editBoundary : area.boundary;
      if (!boundaryToUse || boundaryToUse.length < 3) continue;
      const cat = CAT_MAP.get(area.category);
      const isActive = area.id === activeId;
      const polygon = new google.maps.Polygon({
        paths: boundaryToUse.map((p) => ({ lat: p.lat, lng: p.lon })),
        fillColor: cat?.fill || 'rgba(136,136,136,0.3)',
        fillOpacity: isActive ? 0.5 : 0.3,
        strokeColor: cat?.color || '#888',
        strokeWeight: isActive ? 3 : 2,
        map,
      });
      polygon.addListener('click', () => onAreaClick(area));
      overlaysRef.current.push(polygon);
    }

    // Drawing preview
    if (drawingPath.length >= 2) {
      if (drawingPath.length >= 3) {
        const polygon = new google.maps.Polygon({
          paths: drawingPath,
          fillColor: 'rgba(0,191,255,0.2)',
          fillOpacity: 0.3,
          strokeColor: '#00BFFF',
          strokeWeight: 3,
          map,
        });
        overlaysRef.current.push(polygon);
      } else {
        const polyline = new google.maps.Polyline({
          path: drawingPath,
          strokeColor: '#00BFFF',
          strokeWeight: 3,
          map,
        });
        overlaysRef.current.push(polyline);
      }
    }

    return () => {
      overlaysRef.current.forEach((p) => p.setMap(null));
    };
  }, [map, areas, activeId, drawingPath, editId, editBoundary, onAreaClick]);

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AreasPage() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'none' });
  const [drawingPath, setDrawingPath] = useState<Array<{ lat: number; lng: number }>>([]);

  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('hunting');
  const [editBoundary, setEditBoundary] = useState<Array<{ lat: number; lon: number }>>([]);

  const activeAreaId = panel.type === 'view' || panel.type === 'edit' ? (panel as any).area?.id : null;

  useEffect(() => { loadAreas(); }, []);

  async function loadAreas() {
    setLoading(true);
    const res = await apiFetch<Area[]>('/api/admin/map/areas');
    if (res.success && res.data) setAreas(res.data);
    setLoading(false);
  }

  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (panel.type !== 'drawing') return;
    if (!e.detail.latLng) return;
    setDrawingPath((prev) => [...prev, { lat: e.detail.latLng!.lat, lng: e.detail.latLng!.lng }]);
  }, [panel.type]);

  function startDrawing() {
    setDrawingPath([]);
    setPanel({ type: 'drawing' });
  }

  function finishDrawing() {
    if (drawingPath.length < 3) return;
    setFormName('');
    setFormCategory('hunting');
    setPanel({ type: 'create' });
  }

  function undoPoint() { setDrawingPath((prev) => prev.slice(0, -1)); }
  function cancelDrawing() { setDrawingPath([]); setPanel({ type: 'none' }); }

  function viewArea(area: Area) { setPanel({ type: 'view', area }); }

  function editArea(area: Area) {
    setFormName(area.name);
    setFormCategory(area.category);
    setEditBoundary(area.boundary ? [...area.boundary] : []);
    setPanel({ type: 'edit', area });
  }

  async function saveArea() {
    const boundary = panel.type === 'create'
      ? drawingPath.map((p) => ({ lat: p.lat, lon: p.lng }))
      : panel.type === 'edit' ? editBoundary : [];

    const payload = { name: formName, category: formCategory, boundary };

    if (panel.type === 'edit') {
      await apiFetch(`/api/admin/map/areas/${(panel as any).area.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    } else {
      await apiFetch('/api/admin/map/areas', { method: 'POST', body: JSON.stringify(payload) });
    }
    setDrawingPath([]);
    setPanel({ type: 'none' });
    loadAreas();
  }

  async function deleteArea(area: Area) {
    if (!confirm(`Delete area "${area.name}"?`)) return;
    await apiFetch(`/api/admin/map/areas/${area.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    loadAreas();
  }

  function calcAreaSqm(boundary: Array<{ lat: number; lon: number }>): string {
    if (boundary.length < 3) return '0';
    // Shoelace approximation using local meters
    const toRad = Math.PI / 180;
    const cLat = boundary.reduce((s, p) => s + p.lat, 0) / boundary.length;
    const mPerDegLat = 111320;
    const mPerDegLon = 111320 * Math.cos(cLat * toRad);
    const pts = boundary.map((p) => ({ x: (p.lon - boundary[0].lon) * mPerDegLon, y: (p.lat - boundary[0].lat) * mPerDegLat }));
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      area += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    area = Math.abs(area) / 2;
    if (area > 10000) return `${(area / 10000).toFixed(1)} ha`;
    return `${Math.round(area)} m²`;
  }

  const isDrawing = panel.type === 'drawing';

  return (
    <div style={s.page}>
      <div style={s.mapCol}>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            defaultCenter={PROPERTY_CENTER}
            defaultZoom={15}
            mapTypeId="satellite"
            gestureHandling="greedy"
            fullscreenControl={false}
            mapId="grimsthorpe-admin-areas"
            onClick={handleMapClick}
            style={{ width: '100%', height: '100%', cursor: isDrawing ? 'crosshair' : undefined }}
          >
            <PropertyOverlay />
            {/* Drawing vertex markers */}
            {isDrawing && drawingPath.map((pt, i) => (
              <AdvancedMarker key={i} position={pt}>
                <Pin background={i === 0 ? '#00BFFF' : '#00BFFF'} borderColor="#000" glyphColor="#FFF" scale={0.5} />
              </AdvancedMarker>
            ))}

            {/* Area first-point pins (non-editing) */}
            {!isDrawing && areas.filter((a) => a.boundary?.length > 0).map((area) => {
              const isEditingThis = panel.type === 'edit' && (panel as any).area.id === area.id;
              if (isEditingThis) return null;
              return (
                <AdvancedMarker
                  key={`pin-${area.id}`}
                  position={{ lat: area.boundary[0].lat, lng: area.boundary[0].lon }}
                  title={area.name}
                  onClick={() => viewArea(area)}
                >
                  <Pin background={CAT_MAP.get(area.category)?.color || '#888'} borderColor="#000" glyphColor="#FFF" scale={0.7} />
                </AdvancedMarker>
              );
            })}

            {/* Draggable vertices when editing */}
            {panel.type === 'edit' && editBoundary.map((pt, i) => (
              <AdvancedMarker
                key={`editv-${i}`}
                position={{ lat: pt.lat, lng: pt.lon }}
                draggable
                onDragEnd={(e) => {
                  if (e.latLng) {
                    setEditBoundary((prev) => prev.map((p, j) => j === i ? { lat: e.latLng!.lat(), lon: e.latLng!.lng() } : p));
                  }
                }}
              >
                <Pin background="#00BFFF" borderColor="#00BFFF" glyphColor="#FFF" scale={0.5} />
              </AdvancedMarker>
            ))}

            {/* Info window */}
            {panel.type === 'view' && panel.area.boundary?.length > 0 && (
              <InfoWindow
                position={{ lat: panel.area.boundary[0].lat, lng: panel.area.boundary[0].lon }}
                onCloseClick={() => setPanel({ type: 'none' })}
                pixelOffset={[0, -30]}
              >
                <div style={{ color: '#000', minWidth: 150 }}>
                  <strong>{panel.area.name}</strong>
                  <p style={{ margin: '4px 0', fontSize: 11 }}>
                    {CAT_MAP.get(panel.area.category)?.label} &middot; {panel.area.boundary.length} pts &middot; {calcAreaSqm(panel.area.boundary)}
                  </p>
                  <button onClick={() => editArea(panel.area)}
                    style={{ marginTop: 4, padding: '4px 12px', fontSize: 12, backgroundColor: '#1B3B1B', color: '#FFD700', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Edit Area
                  </button>
                </div>
              </InfoWindow>
            )}

            <AreaPolygons
              areas={areas}
              activeId={activeAreaId}
              drawingPath={isDrawing ? drawingPath : []}
              editId={panel.type === 'edit' ? (panel as any).area.id : null}
              editBoundary={panel.type === 'edit' ? editBoundary : undefined}
              onAreaClick={viewArea}
            />
          </GoogleMap>
        </APIProvider>

        {isDrawing && (
          <div style={s.toolbar}>
            <span style={{ color: '#FFF', fontSize: 13, fontWeight: 500 }}>
              Drawing area &middot; {drawingPath.length} vertex{drawingPath.length !== 1 ? 'es' : ''}
              {drawingPath.length >= 3 && ` · ${calcAreaSqm(drawingPath.map((p) => ({ lat: p.lat, lon: p.lng })))}`}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.toolBtn} onClick={undoPoint} disabled={drawingPath.length === 0}>Undo</button>
              <button style={s.toolBtnDanger} onClick={cancelDrawing}>Cancel</button>
              <button style={s.toolBtnPrimary} onClick={finishDrawing} disabled={drawingPath.length < 3}>
                Finish ({drawingPath.length} pts)
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={s.panelCol}>
        <div style={s.panelContent}>
          {panel.type === 'none' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={s.panelTitle}>Areas</h3>
                <button style={s.btnPrimary} onClick={startDrawing}>+ Draw Area</button>
              </div>
              {loading ? <div style={{ color: COLORS.textMuted }}>Loading...</div> :
                areas.length === 0 ? <p style={{ color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' }}>No areas yet. Click "Draw Area" then click points on the map to define a boundary.</p> :
                areas.map((area) => (
                  <div key={area.id} style={s.itemRow} onClick={() => viewArea(area)}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: CAT_MAP.get(area.category)?.color || '#888', flexShrink: 0 }} />
                    <div>
                      <div style={{ color: COLORS.textPrimary, fontSize: 13, fontWeight: 500 }}>{area.name}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: 11 }}>
                        {CAT_MAP.get(area.category)?.label} &middot; {area.boundary?.length || 0} pts &middot; {calcAreaSqm(area.boundary || [])}
                      </div>
                    </div>
                  </div>
                ))
              }
            </>
          )}

          {panel.type === 'drawing' && (
            <>
              <h3 style={s.panelTitle}>Draw Area</h3>
              <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: '1.6' }}>
                <p style={{ marginBottom: 8 }}><strong style={{ color: COLORS.textPrimary }}>Click</strong> on the map to add boundary vertices.</p>
                <p style={{ marginBottom: 8 }}><strong style={{ color: '#00BFFF' }}>Blue polygon</strong> shows the area preview.</p>
                <p>Click at least 3 points, then hit <strong style={{ color: COLORS.textPrimary }}>Finish</strong>.</p>
              </div>
              {drawingPath.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6 }}>
                  <div style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                    <strong>{drawingPath.length}</strong> vertices
                    {drawingPath.length >= 3 && <> &middot; <strong>{calcAreaSqm(drawingPath.map((p) => ({ lat: p.lat, lon: p.lng })))}</strong></>}
                  </div>
                </div>
              )}
            </>
          )}

          {(panel.type === 'create' || panel.type === 'edit') && (
            <>
              <h3 style={s.panelTitle}>{panel.type === 'edit' ? 'Edit Area' : 'New Area'}</h3>
              {panel.type === 'create' && (
                <div style={{ marginBottom: 12, padding: 10, backgroundColor: COLORS.backgroundTertiary, borderRadius: 6 }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                    {drawingPath.length} vertices &middot; {calcAreaSqm(drawingPath.map((p) => ({ lat: p.lat, lon: p.lng })))}
                  </span>
                </div>
              )}
              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={s.input} autoFocus />
              </div>
              <div style={s.field}>
                <label style={s.label}>Category</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {AREA_CATEGORIES.map((c) => (
                    <button key={c.id} onClick={() => setFormCategory(c.id)}
                      style={{ padding: '6px 14px', borderRadius: 6, border: formCategory === c.id ? `2px solid ${c.color}` : `1px solid ${COLORS.borderDefault}`,
                        backgroundColor: formCategory === c.id ? `${c.color}22` : 'transparent', color: formCategory === c.id ? c.color : COLORS.textMuted, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={s.panelActions}>
                {panel.type === 'edit' && <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteArea(panel.area)}>Delete</button>}
                <button style={s.btnSecondary} onClick={() => { setDrawingPath([]); setPanel({ type: 'none' }); }}>Cancel</button>
                <button style={s.btnPrimary} onClick={saveArea} disabled={!formName.trim()}>
                  {panel.type === 'edit' ? 'Update' : 'Create'}
                </button>
              </div>
            </>
          )}

          {panel.type === 'view' && (
            <>
              <h3 style={s.panelTitle}>{panel.area.name}</h3>
              <span style={{ ...s.badge, backgroundColor: `${CAT_MAP.get(panel.area.category)?.color || '#888'}22`, color: CAT_MAP.get(panel.area.category)?.color || '#888' }}>
                {CAT_MAP.get(panel.area.category)?.label || panel.area.category}
              </span>
              <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 12 }}>
                {panel.area.boundary?.length || 0} vertices &middot; {calcAreaSqm(panel.area.boundary || [])}
              </div>
              <div style={s.panelActions}>
                <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => deleteArea(panel.area)}>Delete</button>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Close</button>
                <button style={s.btnPrimary} onClick={() => editArea(panel.area)}>Edit</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, minWidth: 0, position: 'relative' },
  panelCol: { width: 360, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  toolbar: { position: 'absolute', bottom: 20, left: 20, right: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.85)', borderRadius: 8, zIndex: 10 },
  toolBtn: { padding: '6px 14px', backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFF', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  toolBtnDanger: { padding: '6px 14px', backgroundColor: 'rgba(220,53,69,0.2)', color: '#DC3545', border: '1px solid rgba(220,53,69,0.3)', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  toolBtnPrimary: { padding: '6px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  itemRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', marginBottom: 4, borderRadius: 6, border: `1px solid ${COLORS.borderDefault}`, cursor: 'pointer' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: `1px solid rgba(220,53,69,0.3)`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
};
